import React, {useMemo} from "react";
import {AppInitializer} from "../appInitializer/appInitializer";

/**
 * Check if a feature has been enabled.
 */
export const isFeatureToggleEnabled = (feature: string): boolean => {
  const featureToggles =
    AppInitializer.getInstance().AppConfig?.featureToggles.map(
      (featureToggle) => featureToggle.toUpperCase()
    );
  return featureToggles?.includes(feature.toUpperCase()) ?? false;
};

/**
 * The props for FeatureToggle.
 */
export interface IFeatureToggleProps {
  feature: string;
}

/**
 * The props for FeatureToggle subcomponents, FeatureToggle.On and FeatureToggle.Off.
 */
export interface IFeatureToggleChildProps {
  isFeatureToggleEnabled?: boolean;
}

/**
 * The props for FeatureToggle Custom subcomponent, which allows for a custom condition.
 */
export interface ICustomFeatureToggleChildProps {
  condition: boolean;
  isFeatureToggleEnabled?: boolean;
}

/**
 * The component used to control a feature. It based on AppIntializer.FeatureToggles to turn on/off a feature.
 */
const FeatureToggle: React.FC<IFeatureToggleProps> & {
  On: React.FC<IFeatureToggleChildProps>;
} & {
  Off: React.FC<IFeatureToggleChildProps>;
} & {
  Custom: React.FC<ICustomFeatureToggleChildProps>;
} = ({feature, children}) => {
  const _isFeatureToggleEnabled = useMemo(() => {
    return isFeatureToggleEnabled(feature);
  }, [feature]);

  // Getting the properties of FeatureToggle, this is to retrieve the On and Off properties added.
  const subComponentsList = useMemo(() => Object.keys(FeatureToggle), []);

  const toggledChildren = subComponentsList.map((key) => {
    return React.Children.map(
      children,
      (child) =>
        // Check if the child is On or Off, ignore other child type, such as other components, string, etc., since child is an opaque data structure.
        // displayName is used for comparison due to this issue https://www.albertgao.xyz/2017/10/31/why-this-react-code-not-work-in-production/.
        //@ts-ignore
        (child.type?.displayName ?? "") === key &&
        // Clone On and Off with the provided isFeatureToggledEnabled prop.
        //@ts-ignore
        React.cloneElement(child, {
          isFeatureToggleEnabled: _isFeatureToggleEnabled
        })
    );
  });

  return <>{toggledChildren}</>;
};

/**
 * The component that renders a feature when a feature is turned on. The subcompnent of FeatureToggle.
 */
const On: React.FC<IFeatureToggleChildProps> = ({
  children,
  isFeatureToggleEnabled
}) => {
  return <>{isFeatureToggleEnabled && children}</>;
};
FeatureToggle.On = On;
FeatureToggle.On.displayName = "On";

/**
 * The component that renders another component when a feature is turned off. The subcomponent of FeatureToggle.
 */
const Off: React.FC<IFeatureToggleChildProps> = ({
  children,
  isFeatureToggleEnabled
}) => {
  return <>{!isFeatureToggleEnabled && children}</>;
};
FeatureToggle.Off = Off;
FeatureToggle.Off.displayName = "Off";

/**
 * The component that renders another component when a feature is turned on with a custom condition. The subcomponent of FeatureToggle.
 */
const Custom: React.FC<ICustomFeatureToggleChildProps> = ({
  condition,
  isFeatureToggleEnabled,
  children
}) => {
  return <>{isFeatureToggleEnabled && condition && children}</>;
};
FeatureToggle.Custom = Custom;
FeatureToggle.Custom.displayName = "Custom";

export default FeatureToggle;
